import React, { useState, useEffect } from "react";
import MUIDataTable from "mui-datatables";
import {
  makeStyles,
  createTheme,
  ThemeProvider,
} from "@material-ui/core/styles";

import { THEME_COLOR } from "./../../config/config";

const useStyles = makeStyles((theme) => ({
  highlightedRow: {
    backgroundColor: "#d3d3d3",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
  },
  // Applied to any column with `options.stickyRight: true`: the cell stays
  // pinned to the right edge while the rest of the table scrolls under it.
  // Scoped to td/th because mui-datatables puts the cell class on the <td>
  // AND on an inner div. Backgrounds match the MuiTableCell overrides below.
  stickyRight: {
    "td&, th&": {
      position: "sticky",
      right: 0,
      zIndex: 2,
      backgroundColor: "#fafafa",
    },
    // Header cells render white here (mui-datatables' fixedHeader paper bg
    // wins over the MuiTableCell.head override), so match that.
    "th&": { zIndex: 3, backgroundColor: "#fff" },
    "tr:hover > td&": { backgroundColor: "#f0f0f0" },
  },
}));

const theme = createTheme({
  shadows: Array(5).fill("none"),
  overrides: {
    MUIDataTableHeadCell: {
      root: {
        zIndex: 0,
      },
      fixedHeader: {
        zIndex: 0,
      },
    },
    MuiTableFooter: {
      root: {
        background: "#f0f2f4",
      },
    },
    MuiTableCell: {
      root: {
        fontSize: "12px !important",
        padding: "8px !important",
        backgroundColor: "#fafafa",
        borderBottom: "1px solid #e0e0e0",
      },
      head: {
        fontWeight: "bold !important",
        fontSize: "12px !important",
        backgroundColor: "#f5f5f5",
        color: "#333",
      },
    },
    MuiTable: {
      root: {
        borderCollapse: "separate",
        "@media (max-width: 600px)": {
          fontSize: "12px !important",
        },
      },
    },
    MuiPaper: {
      root: {
        overflowX: "auto",
      },
    },
    MuiToolbar: {
      root: {
        "@media (max-width: 600px)": {
          flexDirection: "column",
          alignItems: "flex-start",
        },
      },
    },
    MuiTablePagination: {
      root: {
        fontSize: "14px",
      },
    },
  },
  MuiFormLabel: {
    zIndex: 0,
  },
  MuiTableHead: { zIndex: 0 },
  palette: {
    primary: {
      main: THEME_COLOR,
    },
  },
});

export default function DataTable(props) {
  const classes = useStyles();
  const { tableData, onRowSelection } = props;
  const [sortedData, setSortedData] = useState(props.tableData || []);
  const [columnStyles, setColumnStyles] = useState({});
  const [selectedRows, setSelectedRows] = React.useState([]);

  const handleRowSelect = (currentRowsSelected, allRows) => {
    if (!allRows || !Array.isArray(allRows)) {
      // console.error("Invalid allRows structure");
      return;
    }

    const selectedIndexes = allRows.map((row) => row.dataIndex);
    const selectedData = selectedIndexes.map((index) => tableData[index]);

    setSelectedRows(selectedData);

    if (onRowSelection) {
      onRowSelection(selectedData);
    }
  };

  useEffect(() => {
    if (props.tableData && props.accountnoFromParams && props.log_type) {
      let matchingRowIndex = -1;

      if (props.log_type === "agreements") {
        matchingRowIndex = props.tableData.findIndex(
          (row) =>
            Object.values(row).includes(props.accountnoFromParams) &&
            Object.values(row).includes(props.agreement_accountno)
        );
      } else {
        matchingRowIndex = props.tableData.findIndex((row) =>
          Object.values(row).includes(props.accountnoFromParams)
        );
      }

      if (matchingRowIndex !== -1) {
        const updatedTableData = [...props.tableData];
        const [matchingRow] = updatedTableData.splice(matchingRowIndex, 1);
        setSortedData([matchingRow, ...updatedTableData]);
      }
    }

    const handleResize = () => {
      const screenWidth = window.innerWidth;
      if (screenWidth > 900) {
        if (props.from === "clients") {
          setColumnStyles({ maxWidth: "150px", whiteSpace: "wrap" });
        } else if (props.from === "agents") {
          setColumnStyles({ whiteSpace: "wrap" });
        } else if (props.from === "rfqs") {
          setColumnStyles({ maxWidth: "150px", whiteSpace: "wrap" });
        } else if (props.from === "orders") {
          setColumnStyles({ maxWidth: "150px", whiteSpace: "wrap" });
        } else if (props.from === "SalesOrders") {
          setColumnStyles({ maxWidth: "150px", whiteSpace: "wrap" });
        } else if (props.from === "quotations") {
          setColumnStyles({ maxWidth: "150px", whiteSpace: "wrap" });
        } else {
          setColumnStyles({ whiteSpace: "wrap" });
        }
      } else {
        setColumnStyles({ whiteSpace: "wrap" });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [props.tableData, props.accountnoFromParams, props.log_type]);

  const pad = (n) => String(n).padStart(2, "0");

  const getDateFormats = (date) => {
    const M = date.getMonth() + 1;
    const D = date.getDate();
    const Y = date.getFullYear();
    const MM = pad(M);
    const DD = pad(D);
    const months = [
      "january", "february", "march", "april", "may", "june",
      "july", "august", "september", "october", "november", "december",
    ];
    const monthsShort = [
      "jan", "feb", "mar", "apr", "may", "jun",
      "jul", "aug", "sep", "oct", "nov", "dec",
    ];
    const mFull = months[date.getMonth()];
    const mShort = monthsShort[date.getMonth()];

    return [
      `${MM}-${DD}-${Y}`,       // 03-12-2026  (USA with leading zeros)
      `${M}-${D}-${Y}`,         // 3-12-2026
      `${MM}/${DD}/${Y}`,       // 03/12/2026  (USA with leading zeros)
      `${M}/${D}/${Y}`,         // 3/12/2026
      `${DD}/${MM}/${Y}`,       // 12/03/2026  (EU format)
      `${Y}-${MM}-${DD}`,       // 2026-03-12  (ISO)
      `${mFull} ${D}, ${Y}`,    // march 12, 2026
      `${mFull} ${DD}, ${Y}`,   // march 12, 2026
      `${mShort} ${D}, ${Y}`,   // mar 12, 2026
      `${mShort} ${DD} ${Y}`,   // mar 12 2026
      `${D} ${mShort} ${Y}`,    // 12 mar 2026
      `${DD} ${mShort} ${Y}`,   // 12 mar 2026
      mFull,                    // march  (month name alone)
      mShort,                   // mar
      String(Y),                // 2026  (year alone)
      `${MM}${DD}${Y}`,         // 03122026  (all digits)
      `${M}${D}${Y}`,           // 3122026
    ];
  };

  let responsive = "vertical";

  const options = {
    filter: true,
    responsive: responsive,
    tableBodyHeight: "100%",
    selectableRows: "none",
    rowsPerPage: 50,
    rowsPerPageOptions: [25, 50, 100],
    jumpToPage: true,
    rowHover: true,
    onRowsDelete: () => false,
    selectToolbar: () => null,
    customSearch: (searchQuery, currentRow, columns) => {
      const q = searchQuery.toLowerCase().trim();
      if (!q) return true;

      for (let i = 0; i < currentRow.length; i++) {
        const value = currentRow[i];
        const colName = columns[i]?.name || "";

        if (colName === "dateCreated" && value) {
          const date = new Date(value);
          if (!isNaN(date)) {
            const formats = getDateFormats(date);

            // Full / partial text match against every format
            if (formats.some((f) => f.toLowerCase().includes(q))) return true;

            // Digit-strip fallback: "03-12-2" → "03122" matches "03122026"
            const qDigits = q.replace(/[^0-9]/g, "");
            if (qDigits.length >= 3) {
              const dateDigits = `${pad(date.getMonth() + 1)}${pad(date.getDate())}${date.getFullYear()}`;
              if (dateDigits.includes(qDigits)) return true;
            }
          }
        }

        // Standard search for all other columns
        if (value !== null && value !== undefined) {
          if (value.toString().toLowerCase().includes(q)) return true;
        }
      }

      return false;
    },
    setRowProps: (row) => {
      if (props.accountnoFromParams && props.tableData) {
        let isAccountInRow = false;

        if (props.log_type === "agreements") {
          isAccountInRow =
            Object.values(row).includes(props.accountnoFromParams) &&
            Object.values(row).includes(props.agreement_accountno);
        } else {
          isAccountInRow = Object.values(row).includes(
            props.accountnoFromParams
          );
        }

        return {
          className: isAccountInRow ? classes.highlightedRow : "",
        };
      }
    },
  };

  const columns = props.columns.map((column) => {
    const sticky = !!(column.options && column.options.stickyRight);
    return {
      ...column,
      options: {
        ...column.options,
        setCellProps: () =>
          sticky
            ? { style: columnStyles, className: classes.stickyRight }
            : { style: columnStyles },
        ...(sticky && {
          setCellHeaderProps: () => ({ className: classes.stickyRight }),
        }),
      },
    };
  });

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.tableContainer}>
        <MUIDataTable
          title={props.title}
          data={sortedData}
          columns={columns}
          options={options}
          id="dTable"
        />
      </div>
    </ThemeProvider>
  );
}